import { useLocation, useNavigate } from "react-router-dom";
import {
  DocumentsPayload,
  RequestStatus,
  RequestType,
} from "../../../interfaces";
import {
  Body,
  BottomActionArea,
  BottomActionButton,
  FullScreenLoader,
  Header,
  PatientSummaryAccordion,
  NavigateBeforeButton,
  ContentContainer as BaseContentContainer,
  PatientSummary as BasePatientSummary,
} from "../../../components";
import { StatusTag } from "../components";
import { HistoricSkeleton } from "./HistoricSkeleton";
import {
  HistoricCompoundedPrescription,
  HistoricMedicalCertificate,
  HistoricMedicalOrder,
  HistoricPrescription,
} from ".";

import { PatientRequestCommentAccordion } from "../../../components/PatientRequestCommentAccordion";
import {
  Box,
  Skeleton,
  Theme,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useRequestData, useRequestSummary } from "../../../hooks";
import { useRepeatRequest } from "../../../hooks/requests/useRepeatRequest";

const ContentContainer = styled(BaseContentContainer)(({ theme }) => ({
  gap: 24,
}));

const PatientSummary = styled(BasePatientSummary)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const PatientSummarySkeleton = styled(BasePatientSummary.Skeleton)(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  }),
);

const HeaderContainer = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  paddingBottom: 0,
  justifyContent: "center",
  [theme.breakpoints.up("md")]: {
    justifyContent: "flex-start",
    padding: 24,
    paddingBottom: 0,
    maxWidth: 1200,
  },
  [theme.breakpoints.down("md")]: {
    paddingBottom: 0,
  },
}));

const Content = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  alignItems: "normal",
  [theme.breakpoints.up("md")]: {
    padding: 24,
    maxWidth: 1200,
    paddingTop: 0,
    paddingBottom: 0,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 0,
  },
}));

const PatientContent = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: 328,
  },
}));

const PrescriptionContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  paddingTop: 0,
  [theme.breakpoints.up("md")]: {
    maxWidth: "calc(min(850px,70%))",
  },
}));

const PatientSummaryAccordionContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const StatusContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  gap: "8px",
}));

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const RejectionWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  padding: "8px 0px",
  gap: "8px",
});

const RejectionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  backgroundColor: "#ffffff",
  padding: "16px",
  gap: "16px",
  whiteSpace: "pre-line",
  color: theme.palette.text.gray,
}));

export function HistoricRequest() {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { requestType } = location.state || {};
  const { data: requestData } = useRequestData();
  const { data: requestSummary, isFetching: isFetchingRequestSummary } =
    useRequestSummary();
  const { handleRepeatRequest, isRepeatingDocument } = useRepeatRequest();
  if (isRepeatingDocument) {
    return (
      <>
        <Header
          title={isMobile ? "Resumen solicitud" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={
            <NavigateBeforeButton
              onClick={() => navigate(`/app/solicitudes`)}
            />
          }
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  if (requestData && requestSummary) {
    return (
      <>
        <Header
          title={isMobile ? "Resumen solicitud" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={
            <NavigateBeforeButton
              onClick={() => navigate(`/app/solicitudes`)}
            />
          }
        />
        <ContentContainer disableGutters>
          <HeaderContainer>
            <Title variant="h5" fontWeight={"medium"}>
              Resumen solicitud
            </Title>
            <StatusTag status={requestSummary.status} fontSize={"16px"} />
          </HeaderContainer>
          <Content>
            <PatientContent>
              <PatientSummary
                patient={requestSummary.patient}
                prevRoute={`/app/solicitudes/${requestSummary.id}/receta/crear`}
                requestId={requestSummary.id}
                canEdit={false}
              />
              <PatientSummaryAccordionContainer>
                <PatientSummaryAccordion
                  patient={requestSummary.patient}
                  isOpen={false}
                  canEdit={false}
                />

                <PatientRequestCommentAccordion
                  request={requestData?.request}
                  canReject={false}
                  isExpanded={true}
                />
              </PatientSummaryAccordionContainer>
            </PatientContent>
            <PrescriptionContent>
              {requestSummary.status === RequestStatus.REJECTED && (
                <RejectionWrapper>
                  <Typography fontWeight={"600"}>Razón de rechazo:</Typography>
                  <RejectionContainer>
                    <Typography>{requestSummary.indications}</Typography>
                  </RejectionContainer>
                </RejectionWrapper>
              )}

              {requestSummary.status === RequestStatus.ACCEPTED && (
                <>
                  <RenderHistoricComponent
                    requestSummary={requestSummary}
                    isFetchingRequestSummary={isFetchingRequestSummary}
                  />
                  <BottomActionArea>
                    <BottomActionButton
                      onClick={() => handleRepeatRequest()}
                      disabled={!Boolean(requestSummary) || isRepeatingDocument}
                    >
                      Repetir solicitud
                    </BottomActionButton>
                  </BottomActionArea>
                </>
              )}
            </PrescriptionContent>
          </Content>
        </ContentContainer>
      </>
    );
  } else {
    return <HistoricRequest.Skeleton requestType={requestType} />;
  }
}

HistoricRequest.Skeleton = function HistoricRequestSkeleton({
  requestType,
}: {
  requestType: RequestType;
}) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const navigate = useNavigate();
  return (
    <>
      <Header
        title={isMobile ? "Resumen solicitud" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={
          <NavigateBeforeButton onClick={() => navigate(`/app/solicitudes`)} />
        }
      />
      <ContentContainer disableGutters>
        <HeaderContainer>
          <Title variant="h5" fontWeight={"medium"}>
            Resumen solicitud
          </Title>
          <StatusContainer>
            <Skeleton variant="rectangular" height={24} width={90} />
          </StatusContainer>
        </HeaderContainer>
        <Content>
          <PatientContent>
            {isMobile ? (
              <PatientSummaryAccordion.Skeleton />
            ) : (
              <PatientSummarySkeleton />
            )}
          </PatientContent>
          <PrescriptionContent>
            <PatientRequestCommentAccordion.Skeleton />
            <HistoricSkeleton requestType={requestType} />
          </PrescriptionContent>
        </Content>
      </ContentContainer>
    </>
  );
};

interface RenderHistoricComponentProps {
  requestSummary: DocumentsPayload;
  isFetchingRequestSummary: boolean;
}

export function RenderHistoricComponent({
  requestSummary,
  isFetchingRequestSummary,
}: RenderHistoricComponentProps) {
  switch (requestSummary.documentType) {
    case RequestType.PRESCRIPTION:
      return (
        <HistoricPrescription
          data={requestSummary}
          isFetching={isFetchingRequestSummary}
        />
      );
    case RequestType.COMPOUNDED_PRESCRIPTION:
      return (
        <HistoricCompoundedPrescription
          data={requestSummary}
          isFetching={isFetchingRequestSummary}
        />
      );
    case RequestType.MEDICAL_CERTIFICATE:
      return <HistoricMedicalCertificate data={requestSummary} />;
    case RequestType.MEDICAL_ORDER:
      return <HistoricMedicalOrder data={requestSummary} />;
    default:
      return <></>;
  }
}
